import { type ReactNode, useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AdminVehicleModelResponse,
  type DeliveryZoneResponse,
  type OrderStatusResponse,
  type RegionResponse,
  type ShopResponse,
  type BrandResponse,
  type CategoryTreeResponse,
  type SearchSynonymResponse,
  type SettingResponse,
  isValidSettingValue,
  toSlug,
} from '@aps/contracts';
import { Button, Skeleton, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { CategoryImageControl } from './CategoryImageControl.js';
import { BannersPanel } from './BannersPanel.js';
import { QueueError } from './WorkQueueScreen.js';

type Tab = 'settings' | 'vehicles' | 'synonyms' | 'banners';

const TABS: Tab[] = ['settings', 'vehicles', 'synonyms', 'banners'];

/**
 * Ma'lumotnomalar — KAMDAN-KAM ochiladigan sozlamalar.
 *
 * Kategoriya/brend, do'kon, zona va statuslar BU YERDAN CHIQARILDI:
 * demoda ular alohida menyu punkti va operator ularni tez-tez
 * ochadi. Bu yerda faqat yiliga bir necha marta tegiladigan narsa
 * qoldi — sozlama, avtomobil katalogi, sinonim, banner.
 *
 * Panellarning O'ZI ko'chirilmadi, faqat eksport qilindi: alohida
 * ekranlar shu yerdagi komponentni chizadi va kod ikkilanmaydi.
 */
export function ReferenceScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [tab, setTab] = useState<Tab>('settings');

  const labels: Record<Tab, string> = {
    settings: t.refSettings,
    vehicles: t.refVehicles,
    synonyms: t.refSynonyms,
    banners: t.refBanners,
  };

  return (
    <section className="adm-page">
      <header className="adm-page__header">
        <h1 className="adm-page__title">{t.navReference}</h1>
      </header>

      <div className="adm-filters__chips">
        {TABS.map((value) => (
          <Button
            key={value}
            variant={tab === value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              setTab(value);
            }}
          >
            {labels[value]}
          </Button>
        ))}
      </div>

      {tab === 'settings' && <SettingsPanel />}
      {tab === 'vehicles' && <VehiclesPanel />}
      {tab === 'synonyms' && <SynonymsPanel />}
      {tab === 'banners' && <BannersPanel />}
    </section>
  );
}

/**
 * Panel sarlavhasini YASHIRISH kaliti.
 *
 * Panel alohida ekranda yolg'iz turganda uning `<h2>` sarlavhasi
 * sahifaning `<h1>` sarlavhasi bilan bir xil matn bo'lib qoladi va
 * ekranda bir so'z ikki marta chiqadi. Bir nechta panel yonma-yon
 * turgan joyda (masalan kategoriya + brend) sarlavha KERAK.
 */
interface PanelProps {
  hideTitle?: boolean;
}

/* ------------------------------ sozlamalar ------------------------------ */

export function SettingsPanel(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });

  if (settings.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (settings.isError) {
    return (
      <QueueError
        message={settings.error instanceof ApiError ? settings.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void settings.refetch()}
      />
    );
  }

  // Guruh bo'yicha yig'amiz — 16 ta sozlama bir uzun ro'yxatda o'qilmaydi.
  const groups = new Map<string, SettingResponse[]>();
  for (const row of settings.data) {
    const list = groups.get(row.group) ?? [];
    list.push(row);
    groups.set(row.group, list);
  }

  return (
    <>
      {[...groups].map(([group, rows]) => (
        <section className="adm-panel" key={group}>
          <h2 className="adm-panel__title">{group}</h2>
          <div className="adm-panel__body">
            {rows.map((row) => (
              <SettingRow key={row.key} setting={row} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function SettingRow({ setting }: { setting: SettingResponse }): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const id = useId();

  const [value, setValue] = useState(setting.value);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(setting.value);
  }, [setting.value]);

  const save = useMutation({
    mutationFn: () => api.updateSetting(setting.key, value),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['settings'] });
      toast.show(t.toastSettingSaved, 'success');
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  // Tekshiruv KONTRAKTDAN — server bilan bir manbadan. Ikki joyda
  // yozilsa forma "to'g'ri" deb turgani holda server 400 qaytarardi.
  const valid = isValidSettingValue(setting.valueType, value);
  const hint = locale === 'RU' ? setting.hintRu : setting.hintUz;

  return (
    <div className="adm-setting">
      <label className="aps-field__label" htmlFor={id}>
        {locale === 'RU' ? setting.labelRu : setting.labelUz}
        {/* Ochiq sozlama mijoz ilovasiga chiqadi — buni bilib turish kerak. */}
        {setting.isPublic && <span className="adm-badge adm-badge--active"> {t.refPublic}</span>}
      </label>

      <div className="adm-setting__row">
        {setting.valueType === 'boolean' ? (
          <select
            id={id}
            className="aps-field__input"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          >
            <option value="true">{t.refYes}</option>
            <option value="false">{t.refNo}</option>
          </select>
        ) : (
          <input
            id={id}
            className="aps-field__input"
            value={value}
            inputMode={setting.valueType === 'number' ? 'decimal' : 'text'}
            onChange={(event) => {
              setValue(event.target.value);
              setError(null);
            }}
          />
        )}
        <Button
          size="sm"
          variant="secondary"
          disabled={!valid || value === setting.value || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {t.orderNoteSave}
        </Button>
      </div>

      <span className="aps-field__hint">
        <code>{setting.key}</code>
        {hint === null ? '' : ` — ${hint}`}
      </span>
      {!valid && <span className="adm-cell-danger">{t.refInvalidType(setting.valueType)}</span>}
      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/* ------------------------------ kategoriyalar ------------------------------ */

interface FlatCategory {
  id: string;
  slug: string;
  nameUz: string;
  nameRu: string | null;
  depth: number;
  isActive: boolean;
  productCount: number;
  imageUrl: string | null;
}

function flatten(nodes: CategoryTreeResponse[]): FlatCategory[] {
  const out: FlatCategory[] = [];
  const walk = (list: CategoryTreeResponse[]): void => {
    for (const node of list) {
      out.push({
        id: node.id,
        slug: node.slug,
        nameUz: node.nameUz,
        nameRu: node.nameRu,
        depth: node.depth,
        isActive: node.isActive,
        productCount: node.productCount ?? 0,
        imageUrl: node.imageUrl,
      });
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

export function CategoriesPanel(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const tree = useQuery({ queryKey: ['categories'], queryFn: () => api.categoryTree() });
  const [nameUz, setNameUz] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['categories'] });
  };
  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };

  const create = useMutation({
    mutationFn: () => {
      const slug = toSlug(nameUz);
      if (slug === null) throw new Error('slug');
      return api.createCategory({
        slug,
        nameUz: nameUz.trim(),
        ...(parentId === '' ? {} : { parentId }),
      });
    },
    onSuccess: () => {
      setNameUz('');
      setError(null);
      refresh();
      toast.show(t.toastAdded, 'success');
    },
    onError,
  });

  const toggle = useMutation({
    mutationFn: (row: FlatCategory) => api.updateCategory(row.id, { isActive: !row.isActive }),
    onSuccess: () => {
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCategory(id),
    onSuccess: () => {
      refresh();
      toast.show(t.toastDeleted, 'success');
    },
    onError,
  });

  if (tree.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (tree.isError) {
    return (
      <QueueError
        message={tree.error instanceof ApiError ? tree.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void tree.refetch()}
      />
    );
  }

  const rows = flatten(tree.data);
  const slug = toSlug(nameUz);

  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{t.refCategories}</h2>
      <div className="adm-panel__body">
        <ul className="adm-reference">
          {rows.map((row) => (
            <li className="adm-reference__row" key={row.id}>
              <span>
                {'— '.repeat(row.depth)}
                {row.nameUz}
                <br />
                <span className="aps-muted">
                  {row.slug} · {t.refProductCount(row.productCount)}
                </span>
              </span>
              <span className={`adm-badge adm-badge--${row.isActive ? 'active' : 'archived'}`}>
                {row.isActive ? t.refActive : t.refInactive}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toggle.mutate(row);
                }}
              >
                {row.isActive ? t.refHide : t.refShow}
              </Button>
              {/*
                Tovari bor kategoriyani server O'CHIRMAYDI. Tugma
                yashirilmaydi: sabab xato matnida aniq aytiladi va
                odam nima qilish kerakligini biladi.
              */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  remove.mutate(row.id);
                }}
              >
                {t.refDelete}
              </Button>
              <CategoryImageControl categoryId={row.id} imageUrl={row.imageUrl} name={row.nameUz} />
            </li>
          ))}
        </ul>

        <h3 className="adm-panel__subtitle">{t.refAddCategory}</h3>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refNameUz}
            value={nameUz}
            onChange={(event) => {
              setNameUz(event.target.value);
              setError(null);
            }}
          />
          <select
            className="aps-field__input"
            value={parentId}
            onChange={(event) => {
              setParentId(event.target.value);
            }}
          >
            <option value="">{t.refNoParent}</option>
            {rows.map((row) => (
              <option key={row.id} value={row.id}>
                {'— '.repeat(row.depth)}
                {row.nameUz}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={slug === null || create.isPending}
            onClick={() => {
              create.mutate();
            }}
          >
            {t.refAdd}
          </Button>
        </div>
        <span className="aps-field__hint">
          {t.refSlugPreview}: <code>{slug ?? '—'}</code>
        </span>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ brendlar ------------------------------ */

export function BrandsPanel(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const brands = useQuery({ queryKey: ['brands'], queryFn: () => api.brands() });
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['brands'] });
  };
  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };

  const create = useMutation({
    mutationFn: () => {
      const slug = toSlug(name);
      if (slug === null) throw new Error('slug');
      return api.createBrand({
        slug,
        name: name.trim(),
        ...(country.trim() === '' ? {} : { country: country.trim() }),
      });
    },
    onSuccess: () => {
      setName('');
      setCountry('');
      setError(null);
      refresh();
      toast.show(t.toastAdded, 'success');
    },
    onError,
  });

  const toggle = useMutation({
    mutationFn: (row: BrandResponse) => api.updateBrand(row.id, { isActive: !row.isActive }),
    onSuccess: () => {
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError,
  });

  if (brands.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (brands.isError) {
    return (
      <QueueError
        message={brands.error instanceof ApiError ? brands.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void brands.refetch()}
      />
    );
  }

  const slug = toSlug(name);

  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{t.refBrands}</h2>
      <div className="adm-panel__body">
        <ul className="adm-reference">
          {brands.data.map((row) => (
            <li className="adm-reference__row" key={row.id}>
              <span>
                {row.name}
                <br />
                <span className="aps-muted">
                  {row.slug}
                  {row.country === null ? '' : ` · ${row.country}`}
                </span>
              </span>
              <span className={`adm-badge adm-badge--${row.isActive ? 'active' : 'archived'}`}>
                {row.isActive ? t.refActive : t.refInactive}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toggle.mutate(row);
                }}
              >
                {row.isActive ? t.refHide : t.refShow}
              </Button>
            </li>
          ))}
        </ul>

        <h3 className="adm-panel__subtitle">{t.refAddBrand}</h3>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refBrandName}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refCountry}
            value={country}
            onChange={(event) => {
              setCountry(event.target.value);
            }}
          />
          <Button
            size="sm"
            disabled={slug === null || create.isPending}
            onClick={() => {
              create.mutate();
            }}
          >
            {t.refAdd}
          </Button>
        </div>
        <span className="aps-field__hint">
          {t.refSlugPreview}: <code>{slug ?? '—'}</code>
        </span>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ avtomobillar ------------------------------ */

/**
 * Marka va modellar.
 *
 * Model `aliases` maydoni QIDIRUVGA tushadi: "treker" deb yozgan
 * mijoz "Tracker" ni topishi kerak. Shuning uchun u ro'yxatda
 * ko'rinib turadi va tahrirlanadi.
 */
export function VehiclesPanel(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const [makeId, setMakeId] = useState('');
  const [makeName, setMakeName] = useState('');
  const [modelName, setModelName] = useState('');
  const [yearFrom, setYearFrom] = useState('2015');
  const [yearTo, setYearTo] = useState(String(new Date().getFullYear()));
  const [aliases, setAliases] = useState('');
  const [error, setError] = useState<string | null>(null);

  const makes = useQuery({ queryKey: ['vehicle-makes'], queryFn: () => api.vehicleMakes() });
  const models = useQuery({
    queryKey: ['vehicle-models', makeId],
    queryFn: () => api.vehicleModels(makeId === '' ? undefined : makeId),
  });

  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['vehicle-makes'] });
    void client.invalidateQueries({ queryKey: ['vehicle-models'] });
  };

  const createMake = useMutation({
    mutationFn: () => api.createVehicleMake({ name: makeName.trim() }),
    onSuccess: () => {
      setMakeName('');
      setError(null);
      refresh();
      toast.show(t.toastAdded, 'success');
    },
    onError,
  });

  const createModel = useMutation({
    mutationFn: () =>
      api.createVehicleModel({
        makeId,
        name: modelName.trim(),
        yearFrom: Number(yearFrom),
        yearTo: Number(yearTo),
        ...(aliases.trim() === '' ? {} : { aliases: aliases.trim() }),
      }),
    onSuccess: () => {
      setModelName('');
      setAliases('');
      setError(null);
      refresh();
      toast.show(t.toastAdded, 'success');
    },
    onError,
  });

  const toggleModel = useMutation({
    mutationFn: (row: AdminVehicleModelResponse) =>
      api.updateVehicleModel(row.id, { isActive: !row.isActive }),
    onSuccess: () => {
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError,
  });

  if (makes.isPending || models.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (makes.isError) {
    return (
      <QueueError
        message={makes.error instanceof ApiError ? makes.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void makes.refetch()}
      />
    );
  }

  const yearsValid =
    /^\d{4}$/u.test(yearFrom) && /^\d{4}$/u.test(yearTo) && Number(yearFrom) <= Number(yearTo);

  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{t.refVehicles}</h2>
      <div className="adm-panel__body">
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refMakeName}
            value={makeName}
            onChange={(event) => {
              setMakeName(event.target.value);
              setError(null);
            }}
          />
          <Button
            size="sm"
            disabled={makeName.trim() === '' || createMake.isPending}
            onClick={() => {
              createMake.mutate();
            }}
          >
            {t.refAddMake}
          </Button>
        </div>

        <h3 className="adm-panel__subtitle">{t.refModels}</h3>
        <select
          className="aps-field__input"
          value={makeId}
          onChange={(event) => {
            setMakeId(event.target.value);
          }}
        >
          <option value="">{t.refAllMakes}</option>
          {makes.data.map((make) => (
            <option key={make.id} value={make.id}>
              {make.name}
            </option>
          ))}
        </select>

        <ul className="adm-reference">
          {(models.data ?? []).map((row) => (
            <li className="adm-reference__row" key={row.id}>
              <span>
                {row.makeName} {row.name}
                <br />
                <span className="aps-muted">
                  {row.yearFrom}–{row.yearTo}
                  {row.aliases === null ? '' : ` · ${row.aliases}`}
                </span>
              </span>
              <span className={`adm-badge adm-badge--${row.isActive ? 'active' : 'archived'}`}>
                {row.isActive ? t.refActive : t.refInactive}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toggleModel.mutate(row);
                }}
              >
                {row.isActive ? t.refHide : t.refShow}
              </Button>
            </li>
          ))}
        </ul>

        <h3 className="adm-panel__subtitle">{t.refAddModel}</h3>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refModelName}
            value={modelName}
            onChange={(event) => {
              setModelName(event.target.value);
              setError(null);
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refYearFrom}
            inputMode="numeric"
            value={yearFrom}
            onChange={(event) => {
              setYearFrom(event.target.value.replace(/\D/gu, ''));
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refYearTo}
            inputMode="numeric"
            value={yearTo}
            onChange={(event) => {
              setYearTo(event.target.value.replace(/\D/gu, ''));
            }}
          />
          <Button
            size="sm"
            disabled={
              makeId === '' || modelName.trim() === '' || !yearsValid || createModel.isPending
            }
            onClick={() => {
              createModel.mutate();
            }}
          >
            {t.refAdd}
          </Button>
        </div>
        <input
          className="aps-field__input"
          placeholder={t.refAliases}
          value={aliases}
          onChange={(event) => {
            setAliases(event.target.value);
          }}
        />
        <span className="aps-field__hint">{t.refAliasesHint}</span>
        {makeId === '' && <span className="adm-cell-danger">{t.refPickMake}</span>}
        {!yearsValid && <span className="adm-cell-danger">{t.refYearsInvalid}</span>}

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ sinonimlar ------------------------------ */

/**
 * Qidiruv sinonimlari.
 *
 * Har o'zgarish Meilisearch indeksiga darhol ko'chiriladi — admin
 * "qo'shdim, ishlamayapti" holatiga tushmasligi kerak.
 */
export function SynonymsPanel(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const [term, setTerm] = useState('');
  const [values, setValues] = useState('');
  const [error, setError] = useState<string | null>(null);

  const synonyms = useQuery({ queryKey: ['synonyms'], queryFn: () => api.synonyms() });

  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['synonyms'] });
  };

  const parsed = values
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value !== '');

  const upsert = useMutation({
    mutationFn: () => api.upsertSynonym({ term: term.trim().toLowerCase(), synonyms: parsed }),
    onSuccess: () => {
      setTerm('');
      setValues('');
      setError(null);
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteSynonym(id),
    onSuccess: () => {
      refresh();
      toast.show(t.toastDeleted, 'success');
    },
    onError,
  });

  if (synonyms.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (synonyms.isError) {
    return (
      <QueueError
        message={synonyms.error instanceof ApiError ? synonyms.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void synonyms.refetch()}
      />
    );
  }

  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{t.refSynonyms}</h2>
      <div className="adm-panel__body">
        <p className="aps-field__hint">{t.refSynonymsHint}</p>

        <ul className="adm-reference">
          {synonyms.data.map((row: SearchSynonymResponse) => (
            <li className="adm-reference__row" key={row.id}>
              <span>
                <strong>{row.term}</strong>
                <br />
                <span className="aps-muted">{row.synonyms.join(', ')}</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  remove.mutate(row.id);
                }}
              >
                {t.refDelete}
              </Button>
            </li>
          ))}
        </ul>

        <h3 className="adm-panel__subtitle">{t.refAddSynonym}</h3>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refTerm}
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setError(null);
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refSynonymValues}
            value={values}
            onChange={(event) => {
              setValues(event.target.value);
            }}
          />
          <Button
            size="sm"
            disabled={term.trim() === '' || parsed.length === 0 || upsert.isPending}
            onClick={() => {
              upsert.mutate();
            }}
          >
            {t.refAdd}
          </Button>
        </div>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ do'konlar ------------------------------ */

/**
 * Do'konlar.
 *
 * Do'kon = savdo nuqtasi VA saqlash joyi (alohida ombor yo'q). Shuning
 * uchun uni yashirish qoldiq va jo'natmalarga ta'sir qiladi — server
 * tugallanmagan jo'natmasi bor do'konni yashirishga yo'l qo'ymaydi.
 */
export function ShopsPanel({ hideTitle }: PanelProps = {}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const shops = useQuery({ queryKey: ['admin-shops'], queryFn: () => api.adminShops() });
  const regions = useQuery({ queryKey: ['regions'], queryFn: () => api.regions() });

  const [code, setCode] = useState('');
  const [nameUz, setNameUz] = useState('');
  const [addressUz, setAddressUz] = useState('');
  const [regionId, setRegionId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['admin-shops'] });
    void client.invalidateQueries({ queryKey: ['shops'] });
  };
  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };

  const create = useMutation({
    mutationFn: () =>
      api.createShop({
        code: code.trim().toUpperCase(),
        regionId,
        nameUz: nameUz.trim(),
        addressUz: addressUz.trim(),
        hasCourier: true,
        hasPickup: true,
      }),
    onSuccess: () => {
      setCode('');
      setNameUz('');
      setAddressUz('');
      setError(null);
      refresh();
      toast.show(t.toastAdded, 'success');
    },
    onError,
  });

  const toggle = useMutation({
    mutationFn: (row: ShopResponse) => api.updateShop(row.id, { isActive: !row.isActive }),
    onSuccess: () => {
      setError(null);
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError,
  });

  if (shops.isPending || regions.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (shops.isError) {
    return (
      <QueueError
        message={shops.error instanceof ApiError ? shops.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void shops.refetch()}
      />
    );
  }

  const regionName = (id: string): string =>
    (regions.data ?? []).find((r: RegionResponse) => r.id === id)?.nameUz ?? '—';
  // Kod FAQAT katta harf, raqam va pastki chiziq (`CreateShopSchema`).
  const codeValid = /^[A-Z0-9_]+$/u.test(code.trim().toUpperCase()) && code.trim() !== '';

  return (
    <section className="adm-panel">
      {hideTitle !== true && <h2 className="adm-panel__title">{t.refShops}</h2>}
      <div className="adm-panel__body">
        <ul className="adm-reference">
          {shops.data.map((row) => (
            <li className="adm-reference__row" key={row.id}>
              <span>
                {row.nameUz}
                <br />
                <span className="aps-muted">
                  {row.code} · {regionName(row.regionId)} · {row.addressUz}
                  {row.phone === null ? '' : ` · ${row.phone}`}
                </span>
              </span>
              <span className={`adm-badge adm-badge--${row.isActive ? 'active' : 'archived'}`}>
                {row.isActive ? t.refActive : t.refInactive}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toggle.mutate(row);
                }}
              >
                {row.isActive ? t.refHide : t.refShow}
              </Button>
            </li>
          ))}
        </ul>

        <h3 className="adm-panel__subtitle">{t.refAddShop}</h3>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refShopCode}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.toUpperCase());
              setError(null);
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refNameUz}
            value={nameUz}
            onChange={(event) => {
              setNameUz(event.target.value);
            }}
          />
          <select
            className="aps-field__input"
            value={regionId}
            onChange={(event) => {
              setRegionId(event.target.value);
            }}
          >
            <option value="">{t.refPickRegion}</option>
            {(regions.data ?? []).map((row: RegionResponse) => (
              <option key={row.id} value={row.id}>
                {row.nameUz}
              </option>
            ))}
          </select>
        </div>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refAddress}
            value={addressUz}
            onChange={(event) => {
              setAddressUz(event.target.value);
            }}
          />
          <Button
            size="sm"
            disabled={
              !codeValid ||
              nameUz.trim() === '' ||
              addressUz.trim() === '' ||
              regionId === '' ||
              create.isPending
            }
            onClick={() => {
              create.mutate();
            }}
          >
            {t.refAdd}
          </Button>
        </div>
        <span className="aps-field__hint">{t.refShopCodeHint}</span>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ zonalar ------------------------------ */

/**
 * Yetkazish zonalari.
 *
 * NARX YO'Q, ataylab: operator jo'natishdan oldin telefonda kelishadi
 * (docs/02-MAHSULOT.md). Bu yerda faqat xizmat nomi va taxminiy muddat.
 */
export function ZonesPanel({ hideTitle }: PanelProps = {}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const zones = useQuery({ queryKey: ['admin-zones'], queryFn: () => api.adminZones() });
  const regions = useQuery({ queryKey: ['regions'], queryFn: () => api.regions() });

  const [nameUz, setNameUz] = useState('');
  const [serviceUz, setServiceUz] = useState('');
  const [etaUz, setEtaUz] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['admin-zones'] });
  };
  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };

  const create = useMutation({
    mutationFn: () =>
      api.createZone({
        nameUz: nameUz.trim(),
        serviceUz: serviceUz.trim(),
        etaUz: etaUz.trim(),
        // Yangi zona zaxira EMAS: zaxira bitta bo'lishi kerak va uni
        // ataylab belgilash kerak (server ham buni majburlaydi).
        isFallback: false,
        // Hududlar keyin biriktiriladi — bo'sh zona hech kimga
        // ta'sir qilmaydi.
        regionIds: [],
      }),
    onSuccess: () => {
      setNameUz('');
      setServiceUz('');
      setEtaUz('');
      setError(null);
      refresh();
      toast.show(t.toastAdded, 'success');
    },
    onError,
  });

  const toggle = useMutation({
    mutationFn: (row: DeliveryZoneResponse) => api.updateZone(row.id, { isActive: !row.isActive }),
    onSuccess: () => {
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError,
  });

  if (zones.isPending || regions.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (zones.isError) {
    return (
      <QueueError
        message={zones.error instanceof ApiError ? zones.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void zones.refetch()}
      />
    );
  }

  const regionNames = (ids: readonly string[]): string =>
    ids.length === 0
      ? t.refNoRegions
      : ids
          .map((id) => (regions.data ?? []).find((r: RegionResponse) => r.id === id)?.nameUz ?? '?')
          .join(', ');

  return (
    <section className="adm-panel">
      {hideTitle !== true && <h2 className="adm-panel__title">{t.refZones}</h2>}
      <div className="adm-panel__body">
        <p className="aps-field__hint">{t.refZonesHint}</p>

        <ul className="adm-reference">
          {zones.data.map((row) => (
            <li className="adm-reference__row" key={row.id}>
              <span>
                {row.nameUz}
                <br />
                <span className="aps-muted">
                  {row.serviceUz} · {row.etaUz} · {regionNames(row.regionIds)}
                </span>
              </span>
              {/* Zaxira zona — hech bir zonaga kirmagan hudud uchun. */}
              {row.isFallback && (
                <span className="adm-badge adm-badge--pending">{t.refFallback}</span>
              )}
              <span className={`adm-badge adm-badge--${row.isActive ? 'active' : 'archived'}`}>
                {row.isActive ? t.refActive : t.refInactive}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toggle.mutate(row);
                }}
              >
                {row.isActive ? t.refHide : t.refShow}
              </Button>
            </li>
          ))}
        </ul>

        <h3 className="adm-panel__subtitle">{t.refAddZone}</h3>
        <div className="adm-setting__row">
          <input
            className="aps-field__input"
            placeholder={t.refNameUz}
            value={nameUz}
            onChange={(event) => {
              setNameUz(event.target.value);
              setError(null);
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refZoneService}
            value={serviceUz}
            onChange={(event) => {
              setServiceUz(event.target.value);
            }}
          />
          <input
            className="aps-field__input"
            placeholder={t.refZoneEta}
            value={etaUz}
            onChange={(event) => {
              setEtaUz(event.target.value);
            }}
          />
          <Button
            size="sm"
            disabled={
              nameUz.trim() === '' ||
              serviceUz.trim() === '' ||
              etaUz.trim() === '' ||
              create.isPending
            }
            onClick={() => {
              create.mutate();
            }}
          >
            {t.refAdd}
          </Button>
        </div>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ statuslar ------------------------------ */

/**
 * Status sozlamalari — GRAF EMAS.
 *
 * Yorliq va push sozlanadi; `code`, oqim va tartib TAHRIRLANMAYDI.
 * O'tishlar jadvali faqat ko'rish uchun: graf buyurtma oqimining o'zi
 * va uni panelda tahrirlash imkoni bitta noto'g'ri bosish bilan butun
 * do'konni to'xtatib qo'yardi.
 */
export function StatusesPanel({ hideTitle }: PanelProps = {}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const statuses = useQuery({ queryKey: ['statuses'], queryFn: () => api.statuses() });
  const transitions = useQuery({
    queryKey: ['status-transitions'],
    queryFn: () => api.statusTransitions(),
  });
  const [error, setError] = useState<string | null>(null);

  const toggleNotify = useMutation({
    mutationFn: (row: OrderStatusResponse) => api.updateStatus(row.code, { notify: !row.notify }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['statuses'] });
      toast.show(t.toastSaved, 'success');
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  if (statuses.isPending || transitions.isPending) {
    return <Skeleton height="240px" label={t.loading} />;
  }
  if (statuses.isError) {
    return (
      <QueueError
        message={statuses.error instanceof ApiError ? statuses.error.text(locale) : t.errorTitle}
        retryLabel={t.retry}
        onRetry={() => void statuses.refetch()}
      />
    );
  }

  const outgoing = (code: string): string =>
    (transitions.data ?? [])
      .filter((row) => row.fromCode === code)
      .map((row) => row.toLabelUz)
      .join(', ');

  return (
    <section className="adm-panel">
      {hideTitle !== true && <h2 className="adm-panel__title">{t.refStatuses}</h2>}
      <div className="adm-panel__body">
        <p className="aps-field__hint">{t.refStatusesHint}</p>

        <ul className="adm-reference">
          {statuses.data.map((row) => (
            <li className="adm-reference__row" key={row.code}>
              <span>
                {row.labelUz}
                <br />
                <span className="aps-muted">
                  <code>{row.code}</code>
                  {row.flow === null ? '' : ` · ${row.flow}`}
                  {row.isTerminal ? ` · ${t.refTerminal}` : ''}
                  {outgoing(row.code) === '' ? '' : ` → ${outgoing(row.code)}`}
                </span>
              </span>
              {/*
                Push o'chirilsa mijoz shu bosqichda xabar OLMAYDI.
                Buni ko'rib turish kerak, aks holda "nega xabar
                kelmadi" savoliga javob topilmasdi.
              */}
              <span className={`adm-badge adm-badge--${row.notify ? 'active' : 'archived'}`}>
                {row.notify ? t.refNotifyOn : t.refNotifyOff}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  toggleNotify.mutate(row);
                }}
              >
                {row.notify ? t.refNotifyDisable : t.refNotifyEnable}
              </Button>
            </li>
          ))}
        </ul>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
